import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { callSecurityAdmin } from "@/lib/securityAdmin";
import { useAuth } from "@/hooks/useAuth";

const KEY = "acex_admin_session_id";
const HEARTBEAT_MS = 60_000;

function sessionId() {
  const existing = sessionStorage.getItem(KEY);
  if (existing) return existing;
  const created = crypto.randomUUID();
  sessionStorage.setItem(KEY, created);
  return created;
}

export async function endTrackedAdminSession(reason: string) {
  const id = sessionStorage.getItem(KEY);
  if (!id) return;
  try {
    await callSecurityAdmin("close-own-admin-session", { session_id: id, reason });
  } catch {
    // Signing out must still continue if the closing audit call cannot complete.
  } finally {
    sessionStorage.removeItem(KEY);
  }
}

export function useAdminSessionTracker(enabled: boolean) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const stopped = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    stopped.current = false;
    const id = sessionId();

    const heartbeat = async (start = false) => {
      if (stopped.current) return;
      try {
        const result = await callSecurityAdmin(start ? "start-admin-session" : "heartbeat-admin-session", { session_id: id });
        if (result?.status === "ended" || result?.status === "expired") {
          stopped.current = true;
          sessionStorage.removeItem(KEY);
          toast.error("This admin session was ended by the security team.");
          await signOut();
          navigate("/admin/login", { replace: true });
        }
      } catch (error) {
        console.error("Admin session heartbeat failed", error);
      }
    };

    void heartbeat(true);
    const timer = window.setInterval(() => void heartbeat(false), HEARTBEAT_MS);
    const onVisible = () => { if (document.visibilityState === "visible") void heartbeat(false); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped.current = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, navigate, signOut]);
}